/**
 * BoldTemplate - Impactante, cores vivas, tipografia gigante, glassmorphism
 * Hero full-screen com overlay, gradients e elementos visuais agressivos
 */

import { Zap, Award, Target, Trophy, FileText, Image, Hash } from 'lucide-react';
import { SantinhoTemplateProps, formatMandate, hasSocialLinks } from './types';
import { SocialLinks } from './SocialLinks';
import { GallerySection } from './GallerySection';
import { ApprovedProjectsSection } from './ApprovedProjectsSection';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';

export function BoldTemplate({ tenant }: SantinhoTemplateProps) {
  const { publicPage, theme, name } = tenant;
  const { title, description, content, socialLinks } = publicPage;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: theme.primaryColor, fontFamily: theme.fontFamily || 'inherit' }}
    >
      {/* Full-Screen Hero */}
      <header className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        {content.coverPhoto && (
          <img
            src={content.coverPhoto}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: content.coverPhoto
              ? `linear-gradient(135deg, ${theme.primaryColor}ee 0%, ${theme.secondaryColor}cc 50%, ${theme.primaryColor}99 100%)`
              : `linear-gradient(145deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`,
          }}
        />

        {/* Decorative circles */}
        <div
          className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] rounded-full opacity-10"
          style={{ backgroundColor: 'white' }}
        />
        <div
          className="absolute bottom-[-60px] left-[-60px] w-[300px] h-[300px] rounded-full opacity-10"
          style={{ backgroundColor: 'white' }}
        />

        <div className="container mx-auto px-4 relative z-10 py-20">
          <div className="flex flex-col lg:flex-row items-center gap-12">

            {/* Candidate Photo */}
            <div className="lg:w-1/2 flex justify-center">
              {content.candidatePhoto ? (
                <div className="relative">
                  <div
                    className="absolute -inset-3 rounded-3xl opacity-30 blur-lg"
                    style={{ backgroundColor: 'white' }}
                  />
                  <img
                    src={content.candidatePhoto}
                    alt={name}
                    className="relative w-64 h-64 md:w-80 md:h-80 rounded-3xl object-cover shadow-2xl border-4 border-white/40"
                  />
                </div>
              ) : (
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-3xl shadow-2xl border-4 border-white/30 flex items-center justify-center text-8xl font-black text-white/60 bg-white/10 backdrop-blur-sm">
                  {name.charAt(0)}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="lg:w-1/2 text-center lg:text-left text-white">
              {/* Candidate Number */}
              {content.candidateNumber && (
                <div className="mb-6">
                  {/* Glassmorphism pill */}
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 mb-4">
                    <Hash className="w-5 h-5 opacity-80" />
                    <span className="text-sm font-semibold uppercase tracking-widest opacity-90">
                      Vote
                    </span>
                  </div>
                  <div className="text-8xl md:text-[9rem] font-black leading-none tracking-tighter drop-shadow-2xl">
                    {content.candidateNumber}
                  </div>
                </div>
              )}

              {/* Title */}
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight leading-none drop-shadow-lg">
                {title || name}
              </h1>

              {/* Description */}
              {description && (
                <p className="mt-5 text-xl md:text-2xl text-white/85 font-medium leading-relaxed">
                  {description}
                </p>
              )}

              {/* Social Links */}
              {hasSocialLinks(socialLinks) && (
                <div className="mt-8">
                  <SocialLinks links={socialLinks} color={`${theme.secondaryColor}dd`} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom slash cut */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="white" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L1440 0V80H0Z" />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="bg-white">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto space-y-20">

            {/* Biography */}
            {content.biography && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Sobre
                  </h2>
                </div>
                <div className="space-y-4">
                  {content.biography.split('\n').filter(Boolean).map((p, i) => (
                    <p key={i} className="text-xl text-gray-700 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            )}

            {/* Mandates */}
            {content.mandates && content.mandates.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Experiência
                  </h2>
                </div>
                <div className="space-y-4">
                  {content.mandates.map((mandate, i) => (
                    <div
                      key={i}
                      className="p-6 rounded-2xl text-white font-bold text-xl shadow-md hover:shadow-lg transition-shadow"
                      style={{
                        background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
                      }}
                    >
                      {formatMandate(mandate)}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Proposals */}
            {content.proposals && content.proposals.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Propostas
                  </h2>
                </div>
                <div className="grid gap-5">
                  {content.proposals.map((proposal, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-6 p-6 rounded-2xl border-2 hover:shadow-md transition-all hover:-translate-y-0.5"
                      style={{
                        backgroundColor: `${theme.primaryColor}08`,
                        borderColor: `${theme.primaryColor}20`,
                      }}
                    >
                      <div
                        className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-md"
                        style={{
                          background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
                        }}
                      >
                        {i + 1}
                      </div>
                      <p className="text-xl text-gray-800 pt-3 leading-relaxed">{proposal}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Achievements */}
            {content.achievements && content.achievements.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Conquistas
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {content.achievements.map((achievement, i) => (
                    <div
                      key={i}
                      className="p-6 rounded-2xl border-4 hover:shadow-lg transition-shadow"
                      style={{ borderColor: theme.primaryColor }}
                    >
                      <p className="text-lg text-gray-800 font-medium leading-relaxed">
                        {achievement}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Approved Projects */}
            {content.approvedProjects && content.approvedProjects.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Projetos Aprovados
                  </h2>
                </div>
                <ApprovedProjectsSection
                  projects={content.approvedProjects}
                  primaryColor={theme.primaryColor}
                  variant="cards"
                />
              </section>
            )}

            {/* Gallery */}
            {content.gallery && content.gallery.length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <h2
                    className="text-4xl font-black uppercase tracking-tight"
                    style={{ color: theme.primaryColor }}
                  >
                    Galeria
                  </h2>
                </div>
                <GallerySection images={content.gallery} />
              </section>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="py-10"
        style={{
          background: `linear-gradient(135deg, ${theme.secondaryColor}, ${theme.primaryColor})`,
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/70 text-sm">
            Página criada com{' '}
            <a
              href="https://campanhaflow.com.br"
              className="text-white hover:underline font-bold"
            >
              CampanhaFlow
            </a>
          </p>
        </div>
      </footer>

      {/* Contact Registration Button */}
      <ContactRegistrationButton />

      {/* Webchat widget */}
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
